
import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Layout } from "@/components/layout/Layout";
import { fetchChannels } from "@/utils/supabase/dataFetching";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowRight, Film, Loader2 } from "lucide-react";
import { SEO } from "@/components/seo/SEO";
import { getPageUrl } from "@/utils/seo/urlUtils";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import ChannelCard from "@/components/home/ChannelCard";

const AllChannels = () => {
  const { t } = useLanguage();
  const { theme } = useTheme();
  const [refreshKey, setRefreshKey] = useState(Date.now());
  
  // Update refreshKey when theme changes to force logo image refresh
  useEffect(() => {
    setRefreshKey(Date.now());
  }, [theme]);
  
  const channelsQuery = useQuery({
    queryKey: ['all-channels'],
    queryFn: async () => {
      try {
        const data = await fetchChannels(100);
        return data;
      } catch (error) {
        console.error("Error loading channels:", error);
        toast.error("Failed to load channels");
        return [];
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  const isLoading = channelsQuery.isLoading;
  const channels = channelsQuery.data || [];
  
  return (
    <Layout>
      <SEO 
        title={t('channels.allChannels')} 
        description={t('channels.description')}
        keywords="recut, content channels, licensed videos, content creators, video channels"
        ogUrl={getPageUrl("/channels")}
      />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold flex items-center">
            <Film className="h-6 w-6 mr-2" />
            {t('channels.allChannels')}
          </h1>
          <p className="text-muted-foreground mt-2">
            {t('channels.description')}
          </p>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary mr-2" />
            <span>{t('common.loading')}</span>
          </div>
        ) : channels.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">{t('channels.noChannels')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {channels.map(channel => (
              <ChannelCard key={channel.id} channel={channel} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AllChannels;
